import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import FileExport from '../plugins/fileExport';
import { isNativePlatform, isWeb } from './platform';
import { downloadPDF } from './webFileExport';

interface SavePdfOptions {
  filename: string;
  pdfData: string | Blob;  // Base64 string or Blob
  mimeType?: string;
}

/**
 * Save a PDF to the device and share it
 * Works on both iOS and Android
 */
export const savePdfToDevice = async (options: SavePdfOptions): Promise<{ success: boolean; path?: string; error?: string }> => {
  const { filename, pdfData } = options;
  const mimeType = options.mimeType || 'application/pdf';
  
  
  
  try {
    // If we're on a native platform
    if (isNativePlatform()) {
      // Try our custom plugin first
      try {
        // Convert to base64 if it's a blob
        let base64Data: string;
        if (typeof pdfData === 'string') {
          // If already a data URI, extract the base64 part
          if (pdfData.startsWith('data:')) {
            base64Data = pdfData.split(',')[1];
          } else {
            base64Data = pdfData;
          }
        } else {
          // Convert Blob to base64
          base64Data = await blobToBase64(pdfData);
        }
        
        // Try our custom plugin which has better file handling
        if (Capacitor.getPlatform() === 'ios') {
          try {
            const result = await FileExport.saveFile({
              filename,
              data: base64Data,
              mimeType
            });
            
            return { 
              success: true, 
              path: result.path
            };
          } catch (pluginError) {
            
            // Fall through to standard approach
          }
        }
        
        // Standard Capacitor approach (works on Android and iOS)
        const savedFile = await Filesystem.writeFile({
          path: filename,
          data: base64Data,
          directory: Directory.Cache,
          recursive: true
        });
        
        
        
        // Share the file
        await Share.share({
          title: filename,
          text: 'PDF Document',
          url: savedFile.uri,
          dialogTitle: 'Save or Share PDF'
        });
        
        return {
          success: true,
          path: savedFile.uri
        };
      } catch (error) {
        
        throw error;
      }
    } else {
      // For web, use our improved web file export utility
      try {
        const result = await downloadPDF(filename, pdfData);
        return result;
      } catch (webError) {
        throw new Error(`Web download error: ${webError.message || 'Unknown error'}`);
      }
    }
  } catch (error) {
    
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error saving PDF'
    };
  }
};

/**
 * Convert a Blob to a base64 string
 */
const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64data = reader.result as string;
      resolve(base64data.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * Convert a base64 string to a Blob
 */
const base64ToBlob = (base64: string, mimeType: string): Blob => {
  // If the base64 string includes a data URI prefix, remove it
  const base64Data = base64.includes('base64,') 
    ? base64.split('base64,')[1] 
    : base64;
  
  const byteCharacters = atob(base64Data);
  const byteArrays = [];
  
  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }
  
  return new Blob(byteArrays, { type: mimeType });
}; 